import React, { useState, useEffect } from 'react';
import { bookings } from '../../api';
import CoachingPlanCard from './CoachingPlanCard';
import LoadingSkeleton from '../LoadingSkeleton';
import './Bookings.css';

export default function CoachingPlansList({ students = [], onPlansChanged }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bookings.getCoachingPlans();
      setPlans(response.data || []);
    } catch (err) {
      console.error('❌ Error loading plans:', err);
      setError(err.response?.data?.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  // Lets the parent's own plan-count badge (shown on the collapsed
  // "Coaching Plans" button) stay accurate after a mutation made from
  // inside this already-expanded list, not just after creating a new plan.
  const reload = async () => {
    await loadPlans();
    if (onPlansChanged) onPlansChanged();
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Delete this coaching plan? All associated sessions will be archived.')) {
      return;
    }

    try {
      await bookings.deleteCoachingPlan(planId);
      await reload();
    } catch (err) {
      console.error('❌ Error deleting plan:', err);
      setError(err.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleMarkSession = async (planId, sequenceNumber, status) => {
    try {
      await bookings.markPlanSession(planId, sequenceNumber, status);
      await reload();
    } catch (err) {
      console.error('❌ Error marking session:', err);
      setError(err.response?.data?.message || 'Failed to update session');
    }
  };

  const handleScheduleSession = async (planId, sequenceNumber, startTimeISO) => {
    try {
      await bookings.schedulePlanSession(planId, sequenceNumber, { startTime: startTimeISO });
      await reload();
    } catch (err) {
      console.error('❌ Error scheduling session:', err);
      setError(err.response?.data?.message || 'Failed to schedule session');
    }
  };

  const handleAssignPlan = async (planId, playerId) => {
    try {
      await bookings.assignCoachingPlan(planId, playerId);
      await reload();
    } catch (err) {
      console.error('❌ Error assigning plan:', err);
      setError(err.response?.data?.message || 'Failed to assign plan');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px 0' }}>
        <LoadingSkeleton type="card" count={3} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        background: '#fadbd8',
        borderRadius: '8px',
        color: '#e74c3c',
        marginBottom: '16px'
      }}>
        ⚠️ {error}
      </div>
    );
  }

  if (plans.length === 0) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        color: '#7f8c8d',
        background: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>📭</div>
        <div>No coaching plans yet. Create one to get started!</div>
      </div>
    );
  }

  // Group plans by status
  const activeLeadingPlans = plans.filter(p => p.bookingStatus === 'available');
  const bookedPlans = plans.filter(p => ['booked', 'in-progress'].includes(p.bookingStatus));
  const completedPlans = plans.filter(p => p.bookingStatus === 'completed');
  const archivedPlans = plans.filter(p => p.bookingStatus === 'archived');

  return (
    <div className="coaching-plans-list">
      {activeLeadingPlans.length > 0 && (
        <>
          <h4 style={{
            color: '#2c3e50',
            margin: '0 0 12px 0',
            fontSize: '14px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>🟢 Available Plans</h4>
          {activeLeadingPlans.map(plan => (
            <CoachingPlanCard
              key={plan._id}
              plan={plan}
              onDelete={handleDeletePlan}
              onAssign={handleAssignPlan}
              students={students}
              showActions={true}
              isCoach={true}
            />
          ))}
        </>
      )}

      {bookedPlans.length > 0 && (
        <>
          <h4 style={{
            color: '#3498db',
            margin: '20px 0 12px 0',
            fontSize: '14px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>📖 Active Plans</h4>
          {bookedPlans.map(plan => (
            <CoachingPlanCard
              key={plan._id}
              plan={plan}
              showActions={false}
              isCoach={true}
              onMarkSession={handleMarkSession}
              onScheduleSession={handleScheduleSession}
            />
          ))}
        </>
      )}

      {completedPlans.length > 0 && (
        <>
          <h4 style={{
            color: '#27ae60',
            margin: '20px 0 12px 0',
            fontSize: '14px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>✅ Completed Plans</h4>
          {completedPlans.map(plan => (
            <CoachingPlanCard
              key={plan._id}
              plan={plan}
              showActions={false}
              isCoach={true}
              onMarkSession={handleMarkSession}
              onScheduleSession={handleScheduleSession}
            />
          ))}
        </>
      )}

      {archivedPlans.length > 0 && (
        <>
          <h4 style={{
            color: '#7f8c8d',
            margin: '20px 0 12px 0',
            fontSize: '14px',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>📦 Archived Plans</h4>
          {archivedPlans.map(plan => (
            <CoachingPlanCard
              key={plan._id}
              plan={plan}
              showActions={false}
              isCoach={true}
            />
          ))}
        </>
      )}
    </div>
  );
}
